import { initContract } from "@ts-rest/core";
import { z } from "zod";

import { ImpactedOptimizationSchema } from "../domain/impactedOptimization";
import {
  CompanyMachineSchema,
  FileSchema,
  LayoutSchema,
  MachineSchema,
  MachineTimesOptimizationMetadataSchema,
  MaterialLayoutSchema,
  OptimizationNotificationSchema,
  OptimizationSchema,
  ProcessStepSchema,
  QuoteRequestLineSchema,
  SheetNestingOptimizationMetadataSchema,
} from "../generated/prisma/zod";
import { ExtendedPartSchema } from "../types/ExtendedPart";
import { NestingRunItemViewSchema } from "./nestingContract";

const c = initContract();

export const OptimizationNestingSettingsSchema = z.object({
  layoutId: z.string(),
  newScrapMaterialBillingRate: z.number(),
});

export type OptimizationNestingSettings = z.infer<typeof OptimizationNestingSettingsSchema>;

const optimizationPartSchema = ExtendedPartSchema.extend({
  files: FileSchema.array(),
});

const optimizationNotificationSchema = OptimizationNotificationSchema.extend({
  part: optimizationPartSchema,
});

const optimizationLineSchema = QuoteRequestLineSchema.extend({
  part: optimizationPartSchema,
  processSteps: ProcessStepSchema.array(),
  layout: LayoutSchema.extend({
    materialLayout: MaterialLayoutSchema.nullable(),
  }).nullable(),
});

const fullOptimizationSchema = OptimizationSchema.extend({
  sheetNestingOptimizationMetadata: SheetNestingOptimizationMetadataSchema.extend({
    layout: LayoutSchema.extend({ materialLayout: MaterialLayoutSchema.nullable() }).nullable(),
    nestingRun: z
      .object({
        sheetsUsed: z.number().nullable(),
        items: NestingRunItemViewSchema.array(),
      })
      .nullable(),
  }).nullable(),
  machineTimesOptimizationMetadata: MachineTimesOptimizationMetadataSchema.extend({
    companyMachine: CompanyMachineSchema.extend({
      machine: MachineSchema,
    }),
  }).nullable(),
  quoteRequestLines: optimizationLineSchema.array(),
  optimizationNotifications: optimizationNotificationSchema.array(),
});

export const optimizationsContract = c.router({
  compute: {
    method: "POST",
    path: "/optimizations/compute",
    body: z.object({
      quoteRequestId: z.string(),
    }),
    responses: {
      200: z.object({
        optimizations: fullOptimizationSchema
          .extend({
            compatibleLines: optimizationLineSchema.array(),
          })
          .array(),
      }),
    },
    summary: "Compute quote request optimizations",
  },
  previewLineRemovalImpact: {
    method: "POST",
    path: "/optimizations/impact/line-removal",
    body: z.object({
      lineIds: z.array(z.string().uuid()).min(1),
    }),
    responses: {
      200: z.object({
        optimizations: ImpactedOptimizationSchema.array(),
      }),
    },
    summary: "List the applied optimizations that removing these lines would cancel",
  },
  listEnabled: {
    method: "GET",
    path: "/optimizations",
    query: z.object({
      quoteRequestId: z.string(),
    }),
    responses: {
      200: z.object({
        optimizations: fullOptimizationSchema.array(),
      }),
    },
    summary: "List enabled optimizations of a quote request",
  },
  toggle: {
    method: "PATCH",
    path: "/optimizations/:id/toggle",
    pathParams: z.object({
      id: z.string(),
    }),
    body: z.object({
      enabled: z.boolean(),
    }),
    responses: {
      200: fullOptimizationSchema,
    },
    summary: "Toggle an optimization",
  },
  restore: {
    method: "POST",
    path: "/optimizations/:id/restore",
    pathParams: z.object({
      id: z.string(),
    }),
    body: c.noBody(),
    responses: {
      200: fullOptimizationSchema,
    },
    summary: "Restore a cancelled optimization",
  },
  updateMachineTimes: {
    method: "PATCH",
    path: "/optimizations/:id/machine-times",
    pathParams: z.object({
      id: z.string(),
    }),
    body: z.object({
      machinePreparationTime: z.number(),
      otherMachineTime: z.number(),
    }),
    responses: {
      200: fullOptimizationSchema,
    },
    summary: "Update machine times for an optimization",
  },
  updateNesting: {
    method: "PATCH",
    path: "/optimizations/:id/nesting",
    pathParams: z.object({
      id: z.string(),
    }),
    body: z.object({
      newScrapMaterialBillingRate: z.number().min(0).max(100),
      syncQuoteRequestLines: z.boolean().optional(),
    }),
    responses: {
      200: OptimizationNestingSettingsSchema,
    },
    summary:
      "Update the billed share of the unused material of a sheet nesting optimization, optionally on all its lines",
  },
  removeLine: {
    method: "DELETE",
    path: "/optimizations/:id/lines/:lineId",
    pathParams: z.object({
      id: z.string(),
      lineId: z.string(),
    }),
    body: c.noBody(),
    responses: {
      200: fullOptimizationSchema,
    },
    summary: "Remove a line from an optimization",
  },
  markNotificationsAsRead: {
    method: "POST",
    path: "/optimizations/:id/notifications/mark-as-read",
    pathParams: z.object({
      id: z.string(),
    }),
    body: z.object({
      notificationIds: z.string().array().min(1),
    }),
    responses: {
      200: z.object({
        readAt: z.coerce.date(),
      }),
    },
    summary: "Mark deleted-line notifications of an optimization as read",
  },
  addLines: {
    method: "POST",
    path: "/optimizations/:id/lines",
    pathParams: z.object({
      id: z.string(),
    }),
    body: z.object({
      lineIds: z.string().array().min(1),
    }),
    responses: {
      200: fullOptimizationSchema,
    },
    summary: "Add lines to an optimization",
  },
});
